import Vue from 'vue'
import Vuex from 'vuex'
import {getDashboard, getDashboardRDI, getDashboardInfo } from '@/helpers/api/dashboard'
import {getProjectByID, getProjectsByUser, getProjectSubjects} from '@/helpers/api/project'
import {resetProjectData} from '@/helpers/projectDataReset'
import {beginProjectTransition, getProjectGeneration, getProjectRequest, isCurrentProjectRequest, setCurrentProject} from '@/helpers/projectContext'

Vue.use(Vuex)

const store = new Vuex.Store({
	state: {
		listingType: 1,
		selectedProject: null,
		selectedConsultasInfo: null,
		selectedAdicionalesInfo: null,
		selectedRdiInfo: null,
		projectsList: null,
		closedProjectsList: null,
		projectTypes: null,
		newProjectModal: false,
		editProjectModal: false,
		newCompanyModal: false,
		projectPermissions: null,
		projectSubjects: [],
		loadingProjectSubjects: false,
		closeOpenProjectModal: {
			status: false,
			info: null
		},
		projectProfiles: [],
		userProjectSpecialtiesSubscriptions: [],
		dashboardInfo: null,
		// true mientras getSelectedProject está resolviendo (incluye
		// la carga inicial desde localStorage). Permite distinguir "todavía
		// no sabemos los módulos" de "no hay proyecto seleccionado".
		loadingSelectedProject: false
	},
	mutations: {
		setListingType(state, type){
			state.listingType = type
		},
		setSelectedProject(state, project){
			if(project){
				localStorage.setItem('selectedProjectID', project.id)
			}
			else{
				localStorage.removeItem('selectedProjectID')
			}
			state.selectedProject = project
		},
		setSelectedRdiInfo(state, info){
			state.selectedRdiInfo = info
		},
		setProjectsList(state, info){
			state.projectsList = info
		},
		setClosedProjectsList(state, info){
			state.closedProjectsList = info
		},
		setNewProjectModal(state, status){
			state.newProjectModal = status
		},
		setEditProjectModal(state, status){
			state.editProjectModal = status
		},
		setNewCompanyModal(state, status){
			state.newCompanyModal = status
		},
		setProjectTypes(state, data){
			state.projectTypes = data
		},
		setProjectPermissions(state, permissions){
			state.projectPermissions = permissions
		},
		setProjectSubjects(state, subjects){
			state.projectSubjects = subjects
		},
		setLoadingProjectSubjects(state, status){
			state.loadingProjectSubjects = status
		},
		setCloseOpenProjectModal(state, info){
			state.closeOpenProjectModal = info
		},
		setProjectProfiles(state, profiles){
			state.projectProfiles = profiles
		},
		setUserProjectSpecialtiesSubscriptions(state, subscriptions){
			state.userProjectSpecialtiesSubscriptions = subscriptions
		},
		setDashboardInfo(state, info){
			state.dashboardInfo = info
		},
		setLoadingSelectedProject(state, status){
			state.loadingSelectedProject = status
		},
		resetForProject(state){
			state.selectedProject = null
			state.selectedConsultasInfo = null
			state.selectedAvancesInfo = null
			state.selectedAdicionalesInfo = null
			state.selectedRdiInfo = null
			state.dashboardInfo = null
			state.projectSubjects = []
			state.loadingProjectSubjects = false
			state.projectPermissions = null
			state.projectProfiles = []
			state.userProjectSpecialtiesSubscriptions = []
		}
	},
	getters: {
		// Mientras loadingSelectedProject es true, todos los módulos se
		// reportan como deshabilitados (evita el parpadeo "activo" que se
		// veía en el menú mientras aún no sabemos qué módulos trae el
		// proyecto). Sin proyecto seleccionado (y ya resuelto el loading),
		// se sigue sin bloquear: true.
		isModuleEnabled: state => code => {
			if (state.loadingSelectedProject) return false

			const modules = state.selectedProject?.system_modules
			if (!modules) return true

			const module = modules.find(m => m.code === code)
			return module ? !!module.pivot?.enabled : false
		}
	},
	actions: {
		async getDashboardInfo(context){
			// context.dispatch('getDashboardTasks')
			// context.dispatch('getDashboardRDI')
			const project_id = context.state.selectedProject && context.state.selectedProject.id
      if (!project_id) return
      const request = getProjectRequest(project_id)
      const response = await getDashboardInfo({ project_id })
      if (isCurrentProjectRequest(request.projectId, request.generation) && response.code === 200) {
        context.commit('setDashboardInfo', response.data)
      }
		},
		async getDashboardTasks(context){
			if(context.state.selectedProject){
				context.commit('setSelectedConsultasInfo', null)
				const project_id = context.state.selectedProject.id
				const request = getProjectRequest(project_id)
				const dashboard = await getDashboard({project_id})
				if(isCurrentProjectRequest(request.projectId, request.generation) && dashboard.code === 200){
					context.commit('setSelectedConsultasInfo', {
						title: 'Consultas y Restricciones',
						semaforo_consultas: dashboard.semaforo_consultas,
						total_title: 'Total Consultas',
						circle_graphs: [
							{
								value: dashboard.total_tasks_percent, 
								title: 'Descripción', 
								color: '#EAB261', 
								center_text: dashboard.total_tasks.toString()
							},
							{
								value: dashboard.ended_tasks_percent, 
								title: 'Descripción', 
								color: '#34BFA3', 
								center_text: dashboard.ended_tasks.toString()
							},
							{
								value: dashboard.pending_tasks_percent, 
								title: 'Descripción', 
								color: '#F45771', 
								center_text: dashboard.pending_tasks.toString()
							}
						],
						speed_solution: {value: dashboard.solution_speed, suffix: 's/d'},
						general_status: 'success',
						adicionales: {
							title: 'Promedio',
							total_title: 'Adicionales',
							speed_solution: {value: dashboard.waiting_time, suffix: 'dias'},
							speed_solution_last: {value: dashboard.waiting_time_last, suffix: 'dias'},
							general_status: 'warning',
							graphs: {
								created_by_week: dashboard.created_by_week,
								solved_by_week: dashboard.solved_by_week
							},
						}
					})
				}
			}
		},
		async getDashboardRDI(context){
			if(context.state.selectedProject){
				context.commit('setSelectedRdiInfo', null)
				const project_id = context.state.selectedProject.id
				const request = getProjectRequest(project_id)
				const dashboard = await getDashboardRDI({project_id})
				if(isCurrentProjectRequest(request.projectId, request.generation) && dashboard.code === 200){
					context.commit('setSelectedRdiInfo', {
						title: 'RDI',
						semaforo_rdis: dashboard.semaforo_rdis,
						total_title: 'Total RDI',
						circle_graphs: [
							{
								value: dashboard.total_rdis_percent, 
								title: 'Descripción', 
								color: '#EAB261', 
								center_text: dashboard.total_rdis.toString()
							},
							{
								value: dashboard.ended_rdis_percent, 
								title: 'Descripción', 
								color: '#34BFA3', 
								center_text: dashboard.ended_rdis.toString()
							},
							{
								value: dashboard.pending_rdis_percent, 
								title: 'Descripción', 
								color: '#F45771', 
								center_text: dashboard.pending_rdis.toString()
							}
						],
						speed_solution: {value: dashboard.solution_speed, suffix: 's/d'},
						general_status: 'success',
						adicionales: {
							title: 'Promedio',
							total_title: 'Adicionales',
							speed_solution: {value: dashboard.waiting_time, suffix: 'dias'},
							speed_solution_last: {value: dashboard.waiting_time_last, suffix: 'dias'},
							general_status: 'warning',
							graphs: {
								created_by_week: dashboard.created_by_week,
								solved_by_week: dashboard.solved_by_week
							},
						}
					})
				}
			}
		},
		async getUserProjects(context){
			const projects = await getProjectsByUser()
			if(projects.code === 200){
				context.commit('setProjectsList', projects.projects)
			}
		},
		async getProjectSubjects(context, projectID){
			const request = getProjectRequest(projectID)
			context.commit('setLoadingProjectSubjects', true)
			const subjects = await getProjectSubjects({project_id: projectID})
			if(isCurrentProjectRequest(request.projectId, request.generation) && subjects.code === 200){
				context.commit('setProjectSubjects', subjects.subjects)
			}
			if(isCurrentProjectRequest(request.projectId, request.generation)) context.commit('setLoadingProjectSubjects', false)
		},
		async getSelectedProject(context, projectID){
			context.commit('setLoadingSelectedProject', true)

			const currentProjectID = context.state.selectedProject && context.state.selectedProject.id
			const storedProjectID = parseInt(localStorage.getItem('selectedProjectID')) || null

			// id que "esperamos" terminar seleccionando, solo para decidir si
			// hay que resetear el estado local antes de pedir datos nuevos.
			// El id real/autoritativo es el que confirme la API.
			const expectedProjectID = projectID || storedProjectID
			const isProjectChange = !!expectedProjectID && expectedProjectID !== currentProjectID

			const generation = isProjectChange
				? beginProjectTransition(expectedProjectID)
				: getProjectGeneration()

			if(isProjectChange){
				resetProjectData()
				context.commit('resetForProject')
			}

			// Cuando no tenemos un id concreto todavía (carga inicial sin
			// nada en localStorage), no podemos usar isCurrentProjectRequest
			// con un id -- comparamos generation directamente para saber si
			// esta llamada sigue siendo la vigente.
			const isStaleGeneration = () => generation !== getProjectGeneration()

			// Trae y aplica (o limpia) el proyecto con el id dado, respetando
			// condiciones de carrera vía isCurrentProjectRequest. Reutilizado
			// tanto para un projectID explícito como para el id guardado en
			// localStorage.
			const loadProjectByID = async (id) => {
				const project = await getProjectByID({project_id: id})
				const resolvedID = project.project && project.project.id
				if(!isCurrentProjectRequest(resolvedID || id, generation)) return false

				if(project.code === 200){
					setCurrentProject(project.project.id, generation)
					context.commit('setSelectedProject', project.project)
					context.commit('setProjectPermissions', project.permissions)
					context.commit('setProjectProfiles', project.profiles)
					context.commit('setUserProjectSpecialtiesSubscriptions', project.user_specialties_subscriptions)
					return true
				}

				context.commit('setSelectedProject', null)
				context.commit('setProjectPermissions', null)
				context.commit('setProjectProfiles', [])
				context.commit('setUserProjectSpecialtiesSubscriptions', [])
				return false
			}

			try {
				if(projectID){
					context.commit('setProjectPermissions', null)
					return await loadProjectByID(projectID)
				}

				// Carga inicial/automática: primero la lista de proyectos del
				// usuario, y luego decidimos cuál seleccionar.
				const projects = await getProjectsByUser()
				if(isStaleGeneration()) return false

				if(projects.code === 200){
					context.commit('setProjectsList', projects.projects)
				}

				// Releemos localStorage por si cambió mientras esperábamos
				// getProjectsByUser (mismo comportamiento que antes).
				const storedID = parseInt(localStorage.getItem('selectedProjectID')) || null
				if(storedID){
					return await loadProjectByID(storedID)
				}

				// Sin id guardado: seleccionamos el primer proyecto de la
				// lista (esto es lo que antes quedaba sin ejecutarse).
				const firstProject = projects.code === 200 && projects.projects && projects.projects[0]
				if(firstProject){
					setCurrentProject(firstProject.id, generation)
					context.commit('setSelectedProject', firstProject)
					return true
				}

				return false
			} catch(e) {
				return false
			} finally {
				const stillCurrent = expectedProjectID
					? isCurrentProjectRequest(expectedProjectID, generation)
					: !isStaleGeneration()
				if(stillCurrent){
					context.commit('setLoadingSelectedProject', false)
				}
			}
		}
	}
})

// Promesa cacheada de la carga inicial del proyecto seleccionado (mismo
// patrón que ensureUserLoaded en UserStore.js). El router la espera antes
// de evaluar isModuleEnabled, para no redirigir al Dashboard mientras
// selectedProject/system_modules todavía se están resolviendo (por ejemplo
// al recargar la página parado en /meetings).
//
// Importante: si en tu bootstrap actual (App.vue, main.js, etc.) ya hacés
// store.dispatch('getSelectedProject') para la carga inicial, reemplazá esa
// llamada por ensureProjectLoaded() para que ambos compartan la misma
// promesa y no se dispare la carga dos veces.
let projectLoadPromise = null

export function ensureProjectLoaded() {
	if (!projectLoadPromise) {
		projectLoadPromise = store.dispatch('getSelectedProject')
	}
	return projectLoadPromise
}

export default store